// Achievement progress and unlocks (docs/05 §9). Runs after every successful command: first it updates
// the progress counters the checks need, then it checks every achievement not yet earned.
import { ACHIEVEMENT_LIST, type AchievementCtx } from '../content/achievements';
import { B } from '../content/balance';
import { sayScript } from './dialogue';
import type { Command, GameEvent, GameState } from './types';

function bump(state: GameState, key: string, by = 1): void {
  const c = state.meta.lifetime.counters;
  c[key] = (c[key] ?? 0) + by;
}

/** Progress the checks read: honest perfect pulls, nudge-made wins, ADORING streaks, three hexes, discoveries. */
function track(ctx: AchievementCtx): void {
  const { state, prev, cmd, events } = ctx;
  const run = state.run;
  for (const e of events) {
    // A perfect pull the player made themselves (not Muscle Memory's assist).
    if (e.e === 'SPIN_START' && e.perfect && cmd.t === 'PULL' && cmd.perfect && !cmd.assisted) bump(state, 'honestPerfect');
    if (e.e === 'WINDOW_START' && run) {
      run.flags.adoringStreak = run.mood.temp === 'adoring' ? (run.flags.adoringStreak ?? 0) + 1 : 0;
      delete run.flags.threeHexes;
    }
    if (e.e === 'HEX_APPLIED' && run && run.hexes.length >= 3) run.flags.threeHexes = 1;
  }
  // Surgeon: a nudge that turned a dead spin into a win.
  if (cmd.t === 'NUDGE' && prev.run?.spin?.payout.lte(0)) {
    const after = events.filter((e) => e.e === 'SPIN_RESOLVED').pop();
    if (after && after.e === 'SPIN_RESOLVED' && after.snapshot.payout.gt(0)) bump(state, 'nudgeWins');
  }
  // The Almanac: every symbol that has been in your deck counts as discovered.
  const changed = events.some((e) => e.e === 'RUN_START' || e.e === 'SYMBOL_ADDED' || e.e === 'SYMBOL_TRANSFORMED' || e.e === 'DRAFT_PICKED');
  if (run && changed) {
    const seen = state.meta.almanac.symbols;
    for (const inst of run.deck) if (!seen.includes(inst.defId)) seen.push(inst.defId);
  }
}

export function checkAchievements(prev: GameState, state: GameState, cmd: Command, events: GameEvent[]): void {
  const ctx: AchievementCtx = { state, prev, cmd, events };
  track(ctx);
  const got = state.meta.achievements;
  for (const def of ACHIEVEMENT_LIST) {
    if (got.includes(def.id) || !def.check(ctx)) continue;
    got.push(def.id);
    events.push({ e: 'ACHIEVEMENT', id: def.id });
  }
}

/**
 * The player hasn't touched anything for `minutes` (the UI measures it). The Maw notices; at the Last
 * Supper, three still minutes are a Hunger Strike (A40). Real time stays outside the sim.
 */
export function idle(state: GameState, minutes: number, events: GameEvent[]): string | undefined {
  const run = state.run;
  if (!run || run.phase === 'consumed') return 'no run';
  const flags = run.flags;
  if (run.window.reckoning === 'lastsupper' && run.phase === 'ready' && minutes >= B.HUNGER_STRIKE_MIN && !state.meta.flags.hungerStrike) {
    state.meta.flags.hungerStrike = true;
    sayScript(state, 'hungerStrike', events, 'suspicious');
    return undefined;
  }
  if (minutes >= B.ASLEEP_MIN && !flags.saidAsleep) {
    flags.saidAsleep = 1;
    sayScript(state, 'asleep', events, 'sleeping');
    return undefined;
  }
  if (minutes >= B.IDLE_LINE_MIN && minutes < B.ASLEEP_MIN && !flags.saidIdle) {
    flags.saidIdle = 1;
    sayScript(state, 'idle', events, 'neutral');
  }
  return undefined;
}
